/**
 * Media Log Service
 * 
 * A centralized service for handling media download analytics logging.
 * Provides methods for logging downloads of galleries, single images, and videos.
 * 
 * @module mediaLogService
 * @example
 * import { mediaLogService } from '@/services/mediaLogService';
 * 
 * // Log a gallery download
 * await mediaLogService.logGalleryDownload(this.$store, {
 *   code: 'XCAEDAS',
 *   parkId: 1,
 *   mediaTotal: 30,
 *   downloadLength: 5
 * });
 */

export const mediaLogService = {
  /**
   * Detects the user's device/browser type based on User-Agent string.
   * 
   * @returns {string} The device type - 'Android', 'iPhone', or 'PC'
   * 
   * @example
   * const device = mediaLogService.detectBrowser();
   */
  detectBrowser() {
    let userAgent = navigator.userAgent;
    let device;

    if (userAgent.match(/android/i)) {
      device = "Android";
    } else if (userAgent.match(/iphone/i)) {
      device = "iPhone";
    } else {
      device = "PC";
    }

    return device;
  },

  /**
   * Core method to send media log data to the store.
   * This method is used internally by other logging methods.
   * 
   * @param {Object} store - Vuex store instance (this.$store)
   * @param {Object} params - Log parameters
   * @param {string} params.joinedCode - Download Code
   * @param {string} params.parkId - Park/UDN
   * @param {number} params.mediaTotal - Total number of media items available
   * @param {number} params.mediaDownload - Number of media items being downloaded
   * @param {boolean} params.isGallery - Indicates Photos from a Gallery (true) or Videos(false)
   * 
   * @returns {Promise<void>} Resolves when log is successfully dispatched
   * 
   * @example
   * await mediaLogService.addMediaLog(this.$store, {
   *   code: 'XCAEDAS',
   *   parkId: 1,
   *   mediaTotal: 10,
   *   mediaDownload: 3,
   *   isGallery: true
   * });
   */
  async addMediaLog(store, params) {
    const joinedCode = params.joinedCode != null ? String(params.joinedCode) : "";

    if (!joinedCode)
      return;

    const defaultParams = {
      joinedCode,
      parkId: params.parkId,
      mediaTotal: params.mediaTotal,
      mediaDownload: params.mediaDownload,
      device: this.detectBrowser(),
      isGallery: params.isGallery,
    };
    await store.dispatch("AddMediaLog", defaultParams);
  },

  /**
   * Logs a gallery/album download (multiple photos).
   * Use this when user downloads a ZIP file or  multiple selection photos.
   * 
   * @param {Object} store - Vuex store instance (this.$store)
   * @param {Object} params - Gallery download parameters
   * @param {string} params.code - Download Code
   * @param {string} params.parkId - Park/UDN
   * @param {number} params.mediaTotal - Total number of media items in gallery
   * @param {number} params.downloadLength - Number of items being downloaded
   * 
   * @returns {Promise<void>} Resolves when log is successfully dispatched
   * 
   * @example
   * await mediaLogService.logGalleryDownload(this.$store, {
   *   code: 'XCAEDAS',
   *   parkId: 1,
   *   mediaTotal: 50,
   *   downloadLength: 25
   * });
   */
  logGalleryDownload(store, { code, parkId, mediaTotal, downloadLength }) {
    return this.addMediaLog(store, {
      joinedCode: code,
      parkId: parkId,
      mediaTotal: mediaTotal,
      mediaDownload: downloadLength,
      isGallery: true,
    });
  },

  /**
   * Logs a single image download.
   * Use this when user downloads an individual photo.
   * 
   * @param {Object} store - Vuex store instance (this.$store)
   * @param {Object} params - Single image download parameters
   * @param {string} params.code - Download Code
   * @param {string} params.parkId - Park/UDN
   * @param {number} params.mediaTotal - Total number of media items available (usually 1)
   * 
   * @returns {Promise<void>} Resolves when log is successfully dispatched
   * 
   * @example
   * await mediaLogService.logSingleDownload(this.$store, {
   *   code: 'XCAEDAS',
   *   parkId: 1,
   *   mediaTotal: 1
   * });
   */
  logSingleDownload(store, { code, parkId, mediaTotal }) {
    return this.addMediaLog(store, {
      joinedCode: code,
      parkId: parkId,
      mediaTotal: mediaTotal,
      mediaDownload: 1,
      isGallery: true,
    });
  },

  /**
   * Logs a single video download.
   * Use this when user downloads an individual video file.
   * 
   * @param {Object} store - Vuex store instance (this.$store)
   * @param {Object} params - Single video download parameters
   * @param {string} params.code - Download Code
   * @param {string} params.parkId - Park/UDN
   * @param {number} params.mediaTotal - Total number of media items available (usually 1)
   * 
   * @returns {Promise<void>} Resolves when log is successfully dispatched
   * 
   * @example
   * await mediaLogService.logVideoDownload(this.$store, {
   *   code: 'XCAEDAS',
   *   parkId: 1,
   *   mediaTotal: 1
   * });
   */
  logVideoDownload(store, { code, parkId, mediaTotal }) {
    return this.addMediaLog(store, {
      joinedCode: code,
      parkId: parkId,
      mediaTotal: mediaTotal,
      mediaDownload: 1,
      isGallery: false,
    });
  }
};